<template>
  <!-- Admins can preview v2 (Admin Dashboard → Landing Page). Everyone else always gets the current page. -->
  <HomePageV2 v-if="activeVersion === 'v2'" />
  <HomepageOnlineJobs v-else-if="activeVersion === 'olj'" />
  <HomepageOnlineJobs2 v-else-if="activeVersion === 'olj2'" />
  <div v-else class="bg-background text-foreground">

    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background pointer-events-none"></div>
      <div class="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-primary/5 blur-3xl pointer-events-none"></div>

      <div class="relative max-w-3xl mx-auto px-6 pt-16 pb-10 sm:pt-24 sm:pb-12 text-center">
        <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6 border border-primary/20">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
          Built for Filipino Remote Workers
        </div>

        <h1 class="text-4xl sm:text-5xl lg:text-[3.1rem] font-extrabold text-foreground leading-[1.08] tracking-tight mb-6">
          The <span class="text-primary">AI job search platform</span> for Filipino remote workers.
        </h1>

        <p class="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto">
          Search remote jobs from the Philippines' leading job platforms in one dashboard. Generate AI cover letters, get instant alerts, and apply faster—all from one place.
        </p>

        <div class="flex flex-wrap gap-3 justify-center mb-5">
          <router-link
            to="/register"
            class="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-base font-semibold hover:bg-primary/90 transition-colors shadow-sm"
          >
            Start Your Free 14-Day Trial →
          </router-link>
          <a
            href="#howItWorks"
            class="inline-flex items-center gap-2 bg-background text-foreground border border-border rounded-lg px-6 py-3 text-base font-semibold hover:bg-muted/50 transition-colors"
          >
            See How it Works
          </a>
        </div>

        <div class="flex flex-wrap gap-x-5 gap-y-1.5 justify-center text-sm text-muted-foreground mb-6">
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> 14-day free trial, no credit card required</span>
          <span class="inline-flex items-center gap-1.5"><i class="fas fa-check text-primary text-xs"></i> Credentials stored encrypted</span>
        </div>

        <div class="flex items-start gap-3 rounded-xl border border-border bg-card/60 p-4 max-w-md mx-auto text-left">
          <div class="flex gap-0.5 mt-0.5 shrink-0">
            <i class="fas fa-star text-yellow-400 text-xs" v-for="n in 5" :key="'hero-star-' + n"></i>
          </div>
          <p class="text-sm text-muted-foreground leading-relaxed">
            "{{ testimonials[3].quote }}"
            <span class="block mt-1 text-xs text-muted-foreground/80 italic">{{ testimonials[3].translation }}</span>
            <span class="block mt-1.5 text-xs font-semibold text-foreground">— {{ testimonials[3].name }}</span>
          </p>
        </div>
      </div>

      <div class="relative max-w-3xl mx-auto px-6 pb-8">
        <div class="pt-8 border-t border-border">
          <div class="text-xs font-semibold text-muted-foreground text-center uppercase tracking-widest mb-4">Aggregating listings from</div>
          <div class="flex flex-wrap justify-center gap-2">
            <span v-for="p in platforms" :key="p" class="bg-muted text-foreground/80 text-sm font-medium px-3.5 py-1.5 rounded-full border border-border">{{ p }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Trusted by Filipino freelancers (stats) -->
    <section class="bg-background">
      <div class="max-w-4xl mx-auto px-6 py-12 sm:py-14">
        <div class="text-xs font-bold text-primary text-center uppercase tracking-widest mb-6">Trusted by Filipino Freelancers</div>
        <div class="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat1Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">Filipino Freelancers</div>
          </div>
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat2Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">Jobs Indexed</div>
          </div>
          <div class="text-center py-4 sm:py-0">
            <div class="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">{{ stat3Display }}</div>
            <div class="text-sm text-muted-foreground mt-2">AI Cover Letters Generated</div>
          </div>
        </div>
      </div>
    </section>

    <!-- One Search. Multiple Platforms. -->
    <section id="howItWorks" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-6 sm:p-10 overflow-hidden">
            <div class="absolute -top-16 -right-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="jobFiltersImg" alt="Applypa unified job search across multiple platforms" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-layer-group"></i> One Search. Multiple Platforms.</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">Stop switching between job boards.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Applypa collects remote jobs from JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH into one feed — so you spend less time searching and more time applying.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-layer-group text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">All your job platforms, one dashboard</p>
                <p class="text-sm text-muted-foreground mt-0.5">JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH — in one place.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-sliders text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Only see jobs worth applying to</p>
                <p class="text-sm text-muted-foreground mt-0.5">Filter by keyword, job type, salary, and category — cut the noise before it reaches you.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-bookmark text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Save multiple filter presets</p>
                <p class="text-sm text-muted-foreground mt-0.5">Switch between saved searches instantly — no re-entering criteria every time.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI Cover Letters -->
    <section id="aiCoverLetter" class="py-16 sm:py-24 px-6 bg-background scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row-reverse items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-6 sm:p-10 overflow-hidden">
            <div class="absolute -top-16 -left-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="aiCoverLetterImg" alt="AI cover letter generator" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-file-alt"></i> AI Cover Letters</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">Write personalized cover letters in seconds.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Stop writing the same letter over and over. Applypa reads the job post and drafts a personalized one for you — instantly.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-wand-magic-sparkles text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">One-click generation</p>
                <p class="text-sm text-muted-foreground mt-0.5">Open any supported job post and hit "Generate with AI" — ready in seconds.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-user-pen text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Sounds like you, not a template</p>
                <p class="text-sm text-muted-foreground mt-0.5">Written from your resume and the specific job description — not a generic template.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-pen-to-square text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Matches your skills to each job's requirements</p>
                <p class="text-sm text-muted-foreground mt-0.5">Reads what the job post is actually asking for and highlights how your experience fits.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Instant Alerts -->
    <section id="notifications" class="py-16 sm:py-24 px-6 bg-card scroll-mt-16">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-14">
        <div class="w-full md:w-1/2">
          <div class="relative rounded-3xl border border-border bg-gradient-to-br from-muted to-background p-6 sm:p-10 overflow-hidden">
            <div class="absolute -top-16 -right-16 w-56 h-56 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <img :src="notificationsImg" alt="Telegram, Discord, and email notifications" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div class="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4"><i class="fas fa-bell"></i> Instant Alerts</div>
          <h2 class="text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">Be one of the first applicants.</h2>
          <p class="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">Many remote jobs receive dozens of applicants within the first hour. Applypa notifies you the moment a matching job is posted.</p>
          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fab fa-telegram text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Telegram, Discord &amp; email alerts</p>
                <p class="text-sm text-muted-foreground mt-0.5">Get notified on Telegram, Discord, or email the instant a matching job posts.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-bullseye text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Never miss a matching job</p>
                <p class="text-sm text-muted-foreground mt-0.5">Alerts are tied to your saved filters — no noise.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-wifi text-primary text-sm"></i></div>
              <div>
                <p class="text-sm font-bold text-foreground">Works while you're offline</p>
                <p class="text-sm text-muted-foreground mt-0.5">Applypa monitors job boards around the clock.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Auto Apply — standalone, high-contrast (killer feature). Fixed dark colors on
         purpose (not theme tokens): this band is meant to always read as dark/bold,
         regardless of the site's light/dark mode — using bg-primary here would flip
         to a near-white background in dark mode since --primary inverts per theme. -->
    <section id="autoApply" class="py-16 sm:py-24 px-6 bg-black text-white scroll-mt-16">
      <div class="max-w-5xl mx-auto">
        <div class="flex flex-col md:flex-row items-center gap-10 md:gap-14">
          <div class="w-full md:w-1/2">
            <div class="flex flex-wrap items-center gap-2 mb-4">
              <div class="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full"><i class="fas fa-bolt"></i> Auto Apply</div>
              <div class="inline-flex items-center gap-1.5 bg-amber-400/20 text-amber-100 text-xs font-bold px-3 py-1.5 rounded-full border border-amber-300/40"><i class="fas fa-circle-info"></i> Requires a linked job board account</div>
            </div>
            <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight tracking-tight">Let Applypa apply while you sleep.</h2>
            <p class="text-sm sm:text-base leading-relaxed mb-6 text-white/80">Turn on Auto Apply and Applypa writes a tailored AI cover letter and submits a real application automatically — at the pace you set. It currently works only with a linked job board account; other connected platforms are browse, filter, and alert only.</p>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-link text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">One linked job board, for now</p>
                  <p class="text-sm mt-0.5 text-white/60">Auto Apply submits to your linked job board's listings. It doesn't submit to LinkedIn, Indeed, or other platforms yet.</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-gear text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">Runs automatically, not one-by-one</p>
                  <p class="text-sm mt-0.5 text-white/60">Once it's on, applications go out on your schedule without a manual approval step for each one.</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <div class="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><i class="fas fa-hand text-white text-sm"></i></div>
                <div>
                  <p class="text-sm font-bold text-white">Skip before it sends, or turn it off</p>
                  <p class="text-sm mt-0.5 text-white/60">See every queued application before its send time and skip it, or turn automation off completely, any time.</p>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/2">
            <div class="relative rounded-3xl border border-white/15 bg-white/5 p-6 sm:p-10 overflow-hidden">
              <img :src="automationApplyImg" alt="Auto Apply automation" class="relative w-full h-auto drop-shadow-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">The Difference</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Applying the old way vs. with Applypa</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">Same job hunt. A fraction of the effort.</p>

      <div class="max-w-3xl mx-auto grid sm:grid-cols-2 gap-4">
        <div class="rounded-2xl border border-border bg-muted/40 p-6">
          <div class="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Without Applypa</div>
          <ul class="space-y-3">
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Open multiple job websites</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Refresh all day</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Copy-paste cover letters</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Miss jobs overnight</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-xmark text-red-500 mt-0.5"></i> Apply one job at a time</li>
          </ul>
        </div>
        <div class="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <div class="text-sm font-bold text-primary uppercase tracking-wider mb-4">With Applypa</div>
          <ul class="space-y-3">
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> One dashboard</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> AI cover letters</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Instant alerts</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Auto Apply</li>
            <li class="flex items-start gap-2.5 text-sm text-foreground"><i class="fas fa-check text-emerald-600 mt-0.5"></i> Save hours every week</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Trust strip -->
    <section class="py-12 px-6 bg-muted border-y border-border">
      <div class="max-w-4xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-lock text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">Encrypted credential storage</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Your platform logins are encrypted and never shared with third parties.</p>
        </div>
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-hand-holding-dollar text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">14-Day Free Trial</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Full Premium access for 14 days, no credit card required.</p>
        </div>
        <div class="flex flex-col items-center gap-2">
          <i class="fas fa-users text-primary text-xl"></i>
          <p class="text-sm font-semibold text-foreground">Built with real Filipino freelancers</p>
          <p class="text-xs text-muted-foreground max-w-[220px]">Shaped by feedback from the people who use it every day.</p>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-16 sm:py-24 px-6 bg-background">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Real Users</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">What freelancers are saying</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-sm mx-auto">From real users — emails, comments, and messages.</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">

        <div v-for="(t, index) in testimonials" :key="index" class="bg-card rounded-2xl p-6 border border-border shadow-sm flex flex-col">
          <div class="flex gap-1 mb-3">
            <i class="fas fa-star text-yellow-400 text-sm" v-for="n in 5" :key="'t' + index + n"></i>
          </div>
          <p class="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">"{{ t.quote }}"
            <span v-if="t.translation" class="block mt-1 text-xs text-muted-foreground italic">"{{ t.translation }}"</span>
          </p>
          <div class="text-xs font-semibold text-muted-foreground">— {{ t.name }}</div>
        </div>

      </div>
    </section>

    <!-- Pricing -->
    <section class="py-16 sm:py-24 px-6 bg-card">
      <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">Pricing</div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Try full Premium access for 14 days.</h2>
      <p class="text-base text-muted-foreground text-center mb-12 max-w-lg mx-auto">No credit card required. After that you keep a Free plan, or upgrade to Basic or Premium.</p>

      <div class="max-w-4xl mx-auto grid sm:grid-cols-2 gap-4">
        <div class="rounded-2xl border-2 border-primary bg-background p-6">
          <div class="text-sm font-bold text-primary uppercase tracking-wider mb-1">Basic</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(basicFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in basicBullets" :key="bullet">{{ bullet }}</li>
          </ul>
        </div>
        <div class="rounded-2xl border border-border bg-background p-6">
          <div class="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-1">Premium</div>
          <div class="text-3xl font-extrabold text-foreground mb-4">{{ formatPeso(premiumFeatures.price) }}<span class="text-sm font-medium text-muted-foreground">/mo</span></div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li v-for="bullet in premiumBullets" :key="bullet">{{ bullet }}</li>
          </ul>
        </div>
      </div>
      <div class="text-center mt-8">
        <router-link
          to="/subscription/plans"
          class="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-8 rounded-lg inline-block transition"
        >
          See All Plans
        </router-link>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-16 sm:py-24 px-6 bg-muted">
      <div class="max-w-2xl mx-auto">
        <div class="text-sm font-bold text-primary text-center uppercase tracking-widest mb-2">FAQ</div>
        <h2 class="text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-3 tracking-tight">Common questions</h2>
        <p class="text-base text-muted-foreground text-center mb-10 max-w-sm mx-auto">Everything you need to know before getting started.</p>

        <div class="space-y-3">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="bg-card rounded-xl border border-border overflow-hidden"
          >
            <button
              @click="toggleFaq(index)"
              class="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none group"
            >
              <span class="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{{ faq.question }}</span>
              <i
                class="fas fa-chevron-down text-muted-foreground text-xs transition-transform duration-200 ml-4 shrink-0"
                :class="{ 'rotate-180': openFaq === index }"
              ></i>
            </button>
            <div v-show="openFaq === index" class="px-5 pb-5">
              <p class="text-sm text-muted-foreground leading-relaxed">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA — fixed dark colors (same reason as Auto Apply above): bg-primary would
         flip to a near-white band in dark mode since --primary inverts per theme. -->
    <section class="py-16 sm:py-24 px-6 bg-black text-white text-center">
      <h2 class="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">Your next remote job starts here.</h2>
      <p class="text-base mb-4 max-w-md mx-auto text-white/85">
        Your next remote job could be posted right now. Applypa makes sure you never miss it.
      </p>
      <p class="text-sm mb-8 text-white/60">
        14-day free trial · No credit card required
      </p>
      <div v-if="!isLoggedIn" class="flex flex-col items-center gap-3">
        <a
          href="https://calendly.com/applypa-support/30min"
          target="_blank"
          rel="noopener"
          class="border-2 border-white/40 text-white rounded-xl px-7 py-3.5 text-base font-bold hover:bg-white/10 transition"
        >
          Book a Demo
        </a>
        <div class="flex flex-wrap gap-3 justify-center">
          <router-link to="/login" class="border-2 border-white/40 text-white rounded-xl px-7 py-3.5 text-base font-bold hover:bg-white/10 transition">
            Log In
          </router-link>
          <router-link to="/register" class="bg-white text-black rounded-xl px-7 py-3.5 text-base font-bold hover:opacity-90 transition">
            Start Your Free Trial →
          </router-link>
        </div>
      </div>
      <div v-else>
        <router-link to="/job-filters" class="bg-white text-black rounded-xl px-7 py-3.5 text-sm font-bold hover:opacity-90 transition inline-block">
          Browse Jobs →
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
import { TESTIMONIALS } from '@/data/testimonials';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import authStore from '@/store/auth';
import apiClient from '@/services/api';
import {
  resolvePlanFeatures,
  formatPeso,
  coverLettersBullet,
  autoApplyBullet,
  directApplyBullet,
  useSubscriptionPlans,
} from '@/composables/useSubscriptionPlans';
import aiCoverLetterImg from '@/assets/applypa/ai-cover-letter-hero.png';
import jobFiltersImg from '@/assets/applypa/job-filters-hero.png';
import notificationsImg from '@/assets/applypa/notifications-hero.png';
import automationApplyImg from '@/assets/applypa/Auto-Apply-Hero.png';
import HomePageV2 from '@/views/public/HomePageV2.vue';
import HomepageOnlineJobs from '@/views/public/Homepage_onlinejobs.vue';
import HomepageOnlineJobs2 from '@/views/public/Homepage_onlinejobs2.vue';
import { useLandingVersion, useGuestPreview } from '@/composables/useLandingVersion';

export default {
  name: 'HomePage',
  components: { HomePageV2, HomepageOnlineJobs, HomepageOnlineJobs2 },
  setup() {
    // Admin preview (/?preview=1) shows the guest CTAs visitors see.
    const { isGuestPreview } = useGuestPreview();
    const isLoggedIn = authStore.state.isLoggedIn && !isGuestPreview.value;
    authStore.checkAuthStatus();
    const { activeVersion } = useLandingVersion();

    const platforms = ['JobStreet', 'Cloudstaff', 'Emapta', 'BossJob', 'Kalibrr', 'VAWorkers PH'];

    // Pricing teaser — pulled from the same subscription-plans API as
    // SubscriptionPlansPage.vue so the numbers here can't go stale.
    const { fetchSubscriptionPlans, findPlan } = useSubscriptionPlans();
    const basicFeatures = computed(() => resolvePlanFeatures(findPlan('basic', 30), 'basic'));
    const premiumFeatures = computed(() => resolvePlanFeatures(findPlan('premium', 30), 'premium'));

    const basicBullets = computed(() => [
      coverLettersBullet(basicFeatures.value),
      autoApplyBullet(basicFeatures.value),
      directApplyBullet(basicFeatures.value),
    ].filter(Boolean));
    const premiumBullets = computed(() => [
      coverLettersBullet(premiumFeatures.value),
      autoApplyBullet(premiumFeatures.value),
      premiumFeatures.value.has_priority_support ? 'Priority support' : directApplyBullet(premiumFeatures.value),
    ].filter(Boolean));

    const openFaq = ref(null);
    const toggleFaq = (index) => {
      openFaq.value = openFaq.value === index ? null : index;
    };

    const testimonials = TESTIMONIALS;

    // FAQ answers accurately reflect: Auto Apply requires a linked job board account, runs on schedule
    // with no per-application approval, and the only user controls are skip-before-send and off.
    const faqs = [
      {
        question: 'Which job platforms does Applypa support?',
        answer: 'Applypa aggregates listings from JobStreet, Cloudstaff, Emapta, BossJob, Kalibrr, and VAWorkers PH into one dashboard. Auto Apply currently works only with a linked job board account — the rest are for browsing, filtering, and alerts. LinkedIn, Indeed, and Upwork support is coming soon.',
      },
      {
        question: 'Do I need a job board account to use Applypa?',
        answer: 'Yes. To browse and apply, you\'ll need an account on at least one supported platform. To use Auto Apply specifically, you\'ll need a linked job board account.',
      },
      {
        question: 'Is Applypa free to use?',
        answer: 'Applypa offers a 14-day free trial with full Premium access — no credit card required. After the trial, you move to the Free plan automatically: unlimited applying and 5 AI cover letters a month. Upgrade to Basic for 250 cover letters a month, or to Premium for 500 plus unlimited Auto Apply.',
      },
      {
        question: 'Will employers know I used AI to write my cover letter?',
        answer: 'For manual applications, cover letters are generated from your resume and the specific job description, and you can review and edit before sending. Auto Apply sends its AI-generated letter automatically without a manual edit step — that\'s the tradeoff for running on autopilot.',
      },
      {
        question: 'How does Auto Apply actually work?',
        answer: 'Auto Apply requires a linked job board account. Once turned on, Applypa finds matching listings, writes a tailored AI cover letter, and submits the application automatically on the schedule you set. There\'s no per-application approval step while it\'s running.',
      },
      {
        question: 'Can I stop an application before it\'s sent?',
        answer: 'Yes. Every queued Auto Apply application appears before its scheduled send time, and you can skip it. You can also turn automation off completely at any time.',
      },
      {
        question: 'What kinds of jobs can I apply to?',
        answer: 'Any remote or freelance role from our supported platforms — virtual assistant, customer support, bookkeeping, social media management, data entry, web development, graphic design, and more. You control which job types and salary ranges show up in your filters.',
      },
      {
        question: 'How is Applypa different from applying manually?',
        answer: 'Manual applying means writing a new cover letter for every job and refreshing job boards to catch new postings. Applypa monitors listings across all your connected platforms, alerts you instantly, and generates a tailored cover letter from your resume — so you spend less time on repetitive work.',
      },
      {
        question: 'Is my connected account login safe?',
        answer: 'Yes. Your credentials are encrypted using industry-standard encryption before being stored, used only to fetch listings or submit applications on your behalf, and never shared with third parties.',
      },
    ];

    // Baselines keep the section looking established while real counts are still ramping up.
    // Real API values only ever raise the displayed number, never lower it below the baseline.
    const STAT1_BASELINE = 850;
    const STAT2_BASELINE = 3200;
    const STAT3_BASELINE = 1100;

    const stat1 = ref(STAT1_BASELINE);
    const stat2 = ref(STAT2_BASELINE);
    const stat3 = ref(STAT3_BASELINE);

    const stat1Display = computed(() => stat1.value.toLocaleString() + '+');
    const stat2Display = computed(() => stat2.value.toLocaleString() + '+');
    const stat3Display = computed(() => stat3.value.toLocaleString() + '+');

    let statsInterval = null;

    const fetchStats = async () => {
      try {
        const res = await apiClient.get('/stats/public_stats/');
        if (res.data.freelancers != null) stat1.value = Math.max(STAT1_BASELINE, res.data.freelancers);
        if (res.data.jobs_processed != null) stat2.value = Math.max(STAT2_BASELINE, res.data.jobs_processed);
        if (res.data.cover_letters != null) stat3.value = Math.max(STAT3_BASELINE, res.data.cover_letters);
      } catch {
        // keep previous values on error
      }
    };

    onMounted(() => {
      fetchStats();
      fetchSubscriptionPlans();
      statsInterval = setInterval(fetchStats, 60000);
    });

    onBeforeUnmount(() => clearInterval(statsInterval));

    return {
      activeVersion,
      isLoggedIn,
      platforms,
      basicFeatures,
      premiumFeatures,
      basicBullets,
      premiumBullets,
      formatPeso,
      stat1Display,
      stat2Display,
      stat3Display,
      aiCoverLetterImg,
      jobFiltersImg,
      notificationsImg,
      automationApplyImg,
      testimonials,
      faqs,
      openFaq,
      toggleFaq,
    };
  },
};
</script>
